import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import '../styles.css';
import Header from '../Header';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import heroImg from '../assets/programs/programs-hero.jpg';
import lotusLantern from '../assets/programs/lotus-lantern.jpg';
import motherOfPearl from '../assets/programs/mother-of-pearl.jpg';
import dancheong from '../assets/programs/dancheong.jpg';
import kArchery from '../assets/programs/k-archery.jpg';
import hanjiLantern from '../assets/programs/hanji-lantern.jpg';
import chopsticks from '../assets/programs/chopsticks.jpg';

const PROGRAM_TILES = [
    {
        id: 'chopsticks',
        src: chopsticks,
        alt: 'Chopstick woodcraft workshop',
        back: {
            title: 'Chopsticks',
            subtitle: 'Traditional Korean Woodcraft Experience',
            points: [
                'Carve, sand, and create',
                'A Hands-On Cultural Experience',
                'Feel the Warmth of Woodcraft',
                'Discover Korean Chopstick Culture',
            ],
        },
    },
    {
        id: 'lotus-lantern',
        src: lotusLantern,
        alt: 'Lotus lantern making',
        back: {
            theme: 'light',
            title: 'Lotus Lantern',
            subtitle: 'Traditional Korean Lantern Craft Experience',
            points: [
                'Handmade light timeless tradition',
                'One lantern, One wish',
                'A gentle pause through Korean craft',
                "Inspired by Korea's UNESCO lantern tradition",
            ],
        },
    },
    {
        id: 'dancheong',
        src: dancheong,
        alt: 'Dancheong painting',
        back: {
            title: 'dancheong',
            subtitle: 'Traditional Korean Pattern Art Craft Experience',
            points: [
                'Color, pattern, and harmony',
                'Bring Korean tradition to life with color',
                'A timeless pattern from Korea',
                'Where tradition meets color',
            ],
        },
    },
    {
        id: 'k-archery',
        src: kArchery,
        alt: 'Korean traditional archery',
        back: {
            theme: 'light',
            title: 'K-Archery',
            subtitle: [
                'For warriors, women, scholars, and royals.',
                'A Korean tradition of focus, respect, and beauty',
            ],
            points: [
                'No sights, No devices',
                'When body and bow become one, the arrow knows where to go',
            ],
        },
    },
    {
        id: 'hanji-lantern',
        src: hanjiLantern,
        alt: 'Hanji lantern craft',
        back: {
            title: 'Hanji Lantern',
            subtitle: 'Traditional Korean Paper Craft Experience',
            points: [
                'Discover the story of Hanji',
                'Traditional Korean craftsmanship',
                'Your own hanbok lantern artwork',
                'Soft light through traditional Hanji',
            ],
        },
    },
    {
        id: 'mother-of-pearl',
        src: motherOfPearl,
        alt: 'Mother-of-pearl (najeon) craft',
        back: {
            theme: 'light',
            title: 'mother-of-pearl',
            subtitle: 'Traditional Korean Decorative Craft Experience',
            points: [
                'Create, Shine, Carry tradition',
                'Elegant Korean Craft Experience',
                'Korean heritage in your hands',
                'Tiny pieces, Timeless beauty',
            ],
        },
    },
];

export default function Programs() {
    const [flipped, setFlipped] = useState({});
    const location = useLocation();

    // A click/tap toggles the flip on all devices (state-driven via
    // .is-flipped). Anchor navigation also sets this state programmatically.
    const handleCardTap = (key) => {
        setFlipped((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    useEffect(() => {
        if (location.hash) {
            const id = location.hash.slice(1);
            const el = document.getElementById(id);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth' });
                // Reveal the target card's back face, as if it were tapped
                const tile = PROGRAM_TILES.find((t) => t.id === id);
                if (tile && tile.back) {
                    setFlipped((prev) => ({ ...prev, [tile.alt]: true }));
                }
                return;
            }
        }
        window.scrollTo({ top: 0 });
    }, [location]);

    useEffect(() => {
        document.title = 'Programs - CHOPSTICKSTORY';

        const setMeta = (name, content, prop = false) => {
            const selector = prop
                ? `meta[property="${name}"]`
                : `meta[name="${name}"]`;
            let el = document.querySelector(selector);
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute(prop ? 'property' : 'name', name);
                document.head.appendChild(el);
            }
            el.content = content;
        };

        setMeta(
            'description',
            'Discover the richness of Korean culture through CHOPSTICKSTORY\'s diverse educational programs.'
        );
        setMeta(
            'keywords',
            'Korean cultural programs, cultural education, Korean culture, CHOPSTICKSTORY'
        );
        setMeta('author', 'CHOPSTICKSTORY');
        setMeta('og:title', 'Programs - CHOPSTICKSTORY', true);
        setMeta(
            'og:description',
            'Discover the richness of Korean culture through our diverse educational programs.',
            true
        );
    }, []);

    return (
        <div className="programs-page">
            <Header />

            <PageHero image={heroImg} title="Our programs" />

            <main className="programs-main">
                <p className="programs-intro">
                    Discover the richness of Korean culture through our diverse
                    educational programs
                </p>

                <section
                    className="programs-gallery"
                    aria-label="Program gallery"
                >
                    {PROGRAM_TILES.map((tile) =>
                        tile.back ? (
                            <figure
                                id={tile.id}
                                className={
                                    'program-tile program-tile--flip' +
                                    (flipped[tile.alt] ? ' is-flipped' : '')
                                }
                                key={tile.alt}
                                tabIndex={0}
                                onClick={() => handleCardTap(tile.alt)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleCardTap(tile.alt);
                                    }
                                }}
                            >
                                <div className="program-card__inner">
                                    <div className="program-card__face program-card__front">
                                        <img
                                            src={tile.src}
                                            alt={tile.alt}
                                            loading="lazy"
                                        />
                                        <span className="program-card__front-title">
                                            {tile.back.title}
                                        </span>
                                    </div>
                                    <div
                                        className={
                                            'program-card__face program-card__back' +
                                            (tile.back.theme === 'light'
                                                ? ' program-card__back--light'
                                                : '')
                                        }
                                    >
                                        <h2 className="program-card__title">
                                            {tile.back.title}
                                        </h2>
                                        <p className="program-card__subtitle">
                                            {Array.isArray(tile.back.subtitle)
                                                ? tile.back.subtitle.map(
                                                      (line, i) => (
                                                          <React.Fragment
                                                              key={line}
                                                          >
                                                              {i > 0 && <br />}
                                                              {line}
                                                          </React.Fragment>
                                                      )
                                                  )
                                                : tile.back.subtitle}
                                        </p>
                                        <ul className="program-card__points">
                                            {tile.back.points.map((point) => (
                                                <li key={point}>{point}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </figure>
                        ) : (
                            <figure
                                id={tile.id}
                                className="program-tile"
                                key={tile.alt}
                            >
                                <img
                                    src={tile.src}
                                    alt={tile.alt}
                                    loading="lazy"
                                />
                            </figure>
                        )
                    )}
                </section>
            </main>

            <Footer />
        </div>
    );
}
